import BlockIcon from '@mui/icons-material/Block';
import EditIcon from '@mui/icons-material/Edit';
import { Avatar, Button, Grid, IconButton, LinearProgress, Link, Stack, Tooltip, Typography } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { useEffect, useRef, useState } from 'react';
import SnackbarAlert from 'ui-component/Alerts/SnackbarAlerts';
import { showConfirmAlert } from 'ui-component/Alerts/SweetAlerts';
import LoaderSpinner from 'ui-component/LoaderSpinner';
import MainCard from 'ui-component/cards/MainCard';
import CheckIcon from '@mui/icons-material/Check';
import FirebaseStorageService from 'firebaseService/firebaseStorage';
import ProfessionalFirestoreService from 'views/ProfessionalServiceManagement/services/ServiceProfessional';
import { Box } from '@mui/system';
import { ServiceProfesionalModal } from './components/ServiceProfesionalModal';
import AddIcon from '@mui/icons-material/Add';
import noImage from 'assets/images/default.jpg';

const ProfessionalServiceManagement = () => {
  const [open, setOpen] = useState(false);
  const formikRef = useRef(null);
  const [loader, setLoader] = useState(false);
  const [isLoading, setLoading] = useState(true);
  const [resources, setResources] = useState([]);
  const [resourcesDisabled, setResourcesDisabled] = useState([]);
  const [snackbarData, setSnackbarData] = useState({
    open: false,
    severity: 'success',
    message: '',
    duration: 3000
  });

  const initialStateDataGrid = {
    pagination: {
      paginationModel: {
        pageSize: 15
      }
    }
  };

  const pageSizes = [1, 5, 10, 15, 25, 50];

  const columns = [
    {
      field: 'icon',
      headerName: '',
      flex: 1,
      renderCell: (params) => <Avatar alt={params.row.iconSrc} src={params.row.icon} />
    },
    { field: 'name', headerName: 'Nombre', flex: 2 },
    { field: 'description', headerName: 'Descripción', flex: 3 },
    {
      field: 'documentationUrl',
      headerName: 'Documentación',
      flex: 2,
      renderCell: (params) => (
        <Link underline="hover" target="_blank" rel="noopener noreferrer" href={params.row.documentationUrl}>
          [Ver]
        </Link>
      )
    }
  ];

  const columnsCurrent = [
    ...columns,
    {
      field: 'action',
      headerName: 'Acciones',
      flex: 2,
      renderCell: (params) => (
        <Stack direction="row" justifyContent="center">
          <Tooltip title="Editar">
            <IconButton color="primary" onClick={() => handleEdit(params.row)}>
              <EditIcon />
            </IconButton>
          </Tooltip>
          <Tooltip title="Desactivar">
            <IconButton color="error" onClick={() => handleRemove(params.row)}>
              <BlockIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      )
    }
  ];

  const columnsDisabled = [
    ...columns,
    {
      field: 'action',
      headerName: 'Acciones',
      flex: 2,
      renderCell: (params) => (
        <Stack direction="row" justifyContent="center">
          <Tooltip title="Reactivar">
            <IconButton color="success" onClick={() => handleReactivate(params.row)}>
              <CheckIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      )
    }
  ];

  useEffect(() => {
    setLoading(true);
    const handleResources = async (data, setDataFunction) => {
      const icons = await Promise.all(
        data.map((resource) => {
          if (resource.iconSrc === 'RESOURCES/DEFAULT/default.jpg') {
            return noImage;
          } else {
            return FirebaseStorageService.downloadFile(resource.iconSrc);
          }
        })
      );
      data.forEach((resource, index) => (resource.icon = icons[index]));

      setDataFunction(data);
      setLoader(false);
      setLoading(false);
    };

    const unsubscribe1 = ProfessionalFirestoreService.subscribeToResourcesProfessional((data) => handleResources(data, setResources));
    const unsubscribe2 = ProfessionalFirestoreService.subscribeToResourcesDisabledProfessional((data) =>
      handleResources(data, setResourcesDisabled)
    );

    return () => {
      unsubscribe1();
      unsubscribe2();
    };
  }, []);

  const closeSnackbar = () => {
    setSnackbarData({
      ...snackbarData,
      open: false
    });
  };

  const handleEdit = async (resource) => {
    setOpen(true);
    setLoader(true);
    setTimeout(() => {
      setLoader(false);
      if (formikRef.current) {
        const file = new File([resource.iconSrc], resource.iconSrc);

        file.uploaded = true;

        formikRef.current.resetForm({
          values: {
            id: resource.id,
            name: resource.name,
            description: resource.description,
            documentationUrl: resource.documentationUrl,
            icon: file
          }
        });
      }
    }, 500);
  };

  const handleRemove = async (resource) => {
    const confirmChange = async () => {
      try {
        setLoader(true);
        const result = await ProfessionalFirestoreService.deleteProfessional(resource.id);

        const successMessage = 'Recurso de Professional desactivado con exito!';
        const errorMessage = 'Error al desactivar recurso de Professional:';

        setSnackbarData({
          open: true,
          severity: result.error ? 'error' : 'success',
          message: result.error ? `${errorMessage} ${result.error.message}` : `${successMessage}`
        });
      } catch (error) {
        console.error(error);
        setSnackbarData({
          open: true,
          severity: 'error',
          message: 'Error inesperado'
        });
      } finally {
        setLoader(false);
      }
    };

    showConfirmAlert(
      'Confirmar desactivacion del Recurso',
      `¿Estás seguro de desactivar el recurso "${resource.name}" de ${resource.type}?`,
      'Confirmar',
      'info',
      confirmChange
    );
  };

  const handleReactivate = async (resource) => {
    const confirmChange = async () => {
      try {
        setLoader(true);
        const result = await ProfessionalFirestoreService.reactivateProfessional(resource.id);

        const successMessage = 'Recurso de servicios profesionales reactivado con exito!';
        const errorMessage = 'Error al reactivar recurso de servicios profesionales:';

        setSnackbarData({
          open: true,
          severity: result.error ? 'error' : 'success',
          message: result.error ? `${errorMessage} ${result.error.message}` : `${successMessage}`
        });
      } catch (error) {
        console.error(error);
        setSnackbarData({
          open: true,
          severity: 'error',
          message: 'Error inesperado'
        });
      } finally {
        setLoader(false);
      }
    };

    showConfirmAlert(
      'Confirmar reactivacion del Recurso',
      `¿Estás seguro de reactivar el recurso "${resource.name}" de ${resource.type}?`,
      'Confirmar',
      'info',
      confirmChange
    );
  };

  const onClose = () => {
    setOpen(false);
  };

  const doOpenModal = () => {
    setOpen(true);
  };

  return (
    <>
      <ServiceProfesionalModal
        open={open}
        onClose={onClose}
        formikRef={formikRef}
        setLoading={setLoading}
        setSnackbarData={setSnackbarData}
      />
      <MainCard
        title="Gestión de servicios profesionales"
        style={{ marginTop: '20px', paddingBottom: '20px' }}
        secondary={
          <Grid container justifyContent={'center'}>
            <Box>
              <Button variant="contained" onClick={doOpenModal} startIcon={<AddIcon />}>
                Agregar
              </Button>
            </Box>
          </Grid>
        }
      >
        <Box sx={{ width: '100%' }}>{isLoading && <LinearProgress />}</Box>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography variant="h5" gutterBottom>
              Recursos Activos
            </Typography>
            <DataGrid
              initialState={initialStateDataGrid}
              pageSizeOptions={pageSizes}
              rows={resources}
              columns={columnsCurrent}
              disableRowSelectionOnClick
              labelRowsPerPage="Filas por página"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="h5" gutterBottom>
              Recursos Inactivos
            </Typography>
            <DataGrid
              initialState={initialStateDataGrid}
              pageSizeOptions={pageSizes}
              rows={resourcesDisabled}
              columns={columnsDisabled}
              disableRowSelectionOnClick
              labelRowsPerPage="Filas por página"
            />
          </Grid>
        </Grid>
        <SnackbarAlert {...snackbarData} closeSnackbar={closeSnackbar} />
        <LoaderSpinner isOpen={loader} setIsOpen={setLoader} />
      </MainCard>
    </>
  );
};

export default ProfessionalServiceManagement;
